import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { CommentDto, Country } from "../../../../../../api/Api";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useCountryAssignees } from "../../../../dataHooks/countryQueries";
import { countryAttachmentApi } from "../../../countryAttachmentApi";
import { useCountryLifecycle } from "../../useCountryLifecycle";
import { partyLabel } from "../../parties/utils";

type Props = {
  specificCountry: Country;
};

type FormData = {
  creditCheckTso: string;
  creditCheckTsoAssignee: string;
  creditAmountTso: string;
  creditCurrencyTso: string;
  creditCheckExchange: string;
  creditCheckExchangeAssignee: string;
  creditAmountExchange: string;
  creditCurrencyExchange: string;
  signOffNote: string;
  comments: CommentDto[];
};

/** Every field except the comment thread, so the credit block helper can index formData safely. */
type TextField = Exclude<keyof FormData, "comments">;

/**
 * Split out of Risk. A country can need a credit line towards the TSO and towards the exchange, and
 * Credit Risk owns the check, amount and currency for both. Worked by the Risk role.
 */
export const CreditRisk: FC<Props> = ({ specificCountry }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const lifecycle = specificCountry.creditRiskLifecycle;
  const marketAccess = specificCountry.marketAccessLifecycle;

  const [formData, setFormData] = useState<FormData>({
    creditCheckTso: lifecycle?.creditCheckTso || "",
    creditCheckTsoAssignee: lifecycle?.creditCheckTsoAssignee || "",
    creditAmountTso: lifecycle?.creditAmountTso || "",
    creditCurrencyTso: lifecycle?.creditCurrencyTso || "",
    creditCheckExchange: lifecycle?.creditCheckExchange || "",
    creditCheckExchangeAssignee: lifecycle?.creditCheckExchangeAssignee || "",
    creditAmountExchange: lifecycle?.creditAmountExchange || "",
    creditCurrencyExchange: lifecycle?.creditCurrencyExchange || "",
    signOffNote: lifecycle?.signOffNote || "",
    comments: lifecycle?.comments || []
  });

  const handleClose = () => navigate(`/countries/country/${params.id}`);

  const {
    handleSave,
    handleComplete,
    isSaving,
    progressOverride,
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment
  } = useCountryLifecycle({
    country: specificCountry,
    lifecycle: "creditRiskLifecycle",
    formData,
    setFormData,
    onClose: handleClose
  });

  const riskAssignees = useCountryAssignees("risk");

  const handleChange = (field: TextField, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const open = location.pathname.endsWith("/creditrisk");

  const tsoNeeded = marketAccess?.tso === "needed";
  const exchangeNeeded = marketAccess?.exchange === "needed";

  const canComplete = Boolean(
    (!tsoNeeded || formData.creditCheckTso) &&
      (!exchangeNeeded || formData.creditCheckExchange)
  );

  const creditBlock = (
    title: string,
    statusField: TextField,
    assigneeField: TextField,
    amountField: TextField,
    currencyField: TextField
  ) => (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>

      <Box display="flex" gap={2}>
        <FormControl size="medium" sx={{ flex: 1 }}>
          <InputLabel>Credit check</InputLabel>
          <Select
            label="Credit check"
            value={formData[statusField]}
            onChange={(e) => handleChange(statusField, e.target.value)}
          >
            <MenuItem value="initiated">Initiated</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>

        <Autocomplete
          sx={{ flex: 1 }}
          options={riskAssignees.data || []}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          value={
            riskAssignees.data?.find(
              (a) => a.email === formData[assigneeField]
            ) || null
          }
          onChange={(_, newValue) =>
            handleChange(assigneeField, newValue?.email || "")
          }
          renderInput={(params) => (
            <TextField {...params} label="Assignee" size="medium" />
          )}
        />
      </Box>

      {formData[statusField] === "approved" && (
        <Box display="flex" gap={2}>
          <TextField
            sx={{ flex: 1 }}
            label="Credit amount"
            value={formData[amountField]}
            onChange={(e) => handleChange(amountField, e.target.value)}
            helperText="Leave blank when there is no cap."
          />
          <TextField
            sx={{ flex: 1 }}
            label="Currency"
            value={formData[currencyField]}
            onChange={(e) => handleChange(currencyField, e.target.value)}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Credit Risk"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={countryAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: () => handleSave(),
        onComplete: () => handleComplete(),
        canComplete,
        isSaving
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        {!tsoNeeded && !exchangeNeeded && (
          <Typography variant="body2" color="text.secondary">
            Market Access has not marked a TSO or an exchange as needed, so
            there is no credit line to check.
          </Typography>
        )}

        {tsoNeeded &&
          creditBlock(
            `Credit check ${partyLabel("tso", marketAccess)}`,
            "creditCheckTso",
            "creditCheckTsoAssignee",
            "creditAmountTso",
            "creditCurrencyTso"
          )}

        {exchangeNeeded &&
          creditBlock(
            `Credit check ${partyLabel("exchange", marketAccess)}`,
            "creditCheckExchange",
            "creditCheckExchangeAssignee",
            "creditAmountExchange",
            "creditCurrencyExchange"
          )}

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
